import React, {useState, useCallback, useEffect, useMemo, useRef} from 'react';
import {
  Grid,
  Container,
  Typography,
  CircularProgress,
  Backdrop,
  LinearProgress,
  useTheme,
  Box,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@material-ui/core';
import FadeIn from 'react-fade-in/lib/FadeIn';
import Placeholder from './../misc/Placeholder';
import useStore from '../../store/store';
import useSnackbarSuccessOrError from './subscription/customerSuccessBar';
import {PeriodPicker} from './PeriodPicker';
import {APICall} from '../../api/api';
import StackedReportGraph from './stackedReport/StackedReportGraph';
import DataTypeSelector from './DataTypeSelector';
import usePersistentAuthStore from '../../store/persistentAuthStore';
import usePersistentStore from '../../store/persistentStore';
import {
  convertAnalyticsDatesToLocalTimezone,
  toAnalyticsPeriod,
} from './stackedReport/helpers/dataHelpers';
import {getRealtimeRefreshIntervalSecondsOrDefault} from './stackedReport/helpers/configs';
import {exportStackedReportXls} from './stackedReport/helpers/exportStackedReportXls';
import {fetchPanelChangesBySelection} from './stackedReport/helpers/panelChangeLog';
import GraphControlsMenu from './stackedReport/subcomponents/GraphControlsMenu';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const StackedReport = ({isMobile, login, type, isSelfServe}) => {
  const theme = useTheme();
  const [data, setData] = useState();
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonFetchComplete, setComparisonFetchComplete] = useState(true);
  const [insights, setInsights] = useState(null);
  const [resellerValues, setResellerValues] = useState(null);
  const [resellerCommission, setResellerCommission] = useState(null);
  const [attributeGroups, setAttributeGroups] = useState(null);
  const [panelChanges, setPanelChanges] = useState(null);
  const [panelChangesLoading, setPanelChangesLoading] = useState(false);
  const [sspTypeChanges, setSspTypeChanges] = useState(null);
  const [customLoadingStatus, setCustomLoadingStatus] = useState(null);
  const [exportPayload, setExportPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const dataType = useStore(state => state.dataType);
  const selection = useStore(state => state.selection);
  const period = useStore(state => state.period);
  const comparisonPeriod = useStore(state => state.comparisonPeriod);
  const analyticsCountry = useStore(state => state.analyticsCountryFilter);
  const analyticsDevice = useStore(state => state.analyticsDeviceFilter);
  const analyticsBidder = useStore(state => state.analyticsBidderFilter);
  const setAnalyticsAttributeGroups = useStore(state => state.setState);
  const analyticsGrouping = usePersistentStore(state => state.analyticsGrouping) || 'bidder';
  const groupingEnabled = useStore(state => state.groupingEnabled);
  const prebidSplitMode = useStore(state => state.prebidSplitMode);
  const disabledSources = useStore(state => state.disabledSources);
  const realtimeMinutes = usePersistentStore(state => state.realtimeMinutes);
  const autoRefreshEnabled = usePersistentStore(state => state.autoRefreshEnabled);
  const realtimeRefreshIntervalSecondsRaw = usePersistentStore(
    state => state.realtimeRefreshIntervalSeconds
  );
  const realtimeRefreshIntervalSeconds = getRealtimeRefreshIntervalSecondsOrDefault(
    realtimeRefreshIntervalSecondsRaw
  );
  const comparisonEnabled = usePersistentStore(state => state.comparisonEnabled);
  const analyticsChangeLogEnabled = usePersistentStore(
    state => state.analyticsChangeLogEnabled ?? true
  );
  const abortControllerRef = useRef(null);
  const panelChangesAbortRef = useRef(null);
  const [callSnackbarSuccessError] = useSnackbarSuccessOrError();
  const {auth} = usePersistentAuthStore();
  const isRealtime = period?.label === 'realtime';

  useEffect(() => {
    setResellerCommission(null);
  }, [auth?.accountId]);
  const showServerClientToggle = type === 'prebid';

  // Eagerly clear stale data when period changes so that
  // StackedReportGraph never runs buildDemandData with old data
  // paired with a new period (e.g. daily data + realtime period
  // causes fillMissingDates to generate ~170K 15-second entries).
  const [prevPeriod, setPrevPeriod] = useState(period);
  if (period !== prevPeriod) {
    setPrevPeriod(period);
    if (data != null) setData(undefined);
    if (comparisonData !== null) setComparisonData(null);
    if (comparisonFetchComplete) setComparisonFetchComplete(false);
    if (panelChanges != null) setPanelChanges(null);
    if (sspTypeChanges != null) setSspTypeChanges(null);
  }

  // Fetches realtime raw data from API
  const fetchData = useCallback(
    async ({isAutoRefresh = false} = {}) => {
      // Read tokens at call time so a background refresh does not recreate this
      // callback / re-trigger the effect (that was aborting in-flight retries).
      const auth = usePersistentAuthStore.getState().auth;
      if (!auth?.token) return;

      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create a new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Reset comparison data before new fetch
      setComparisonData(null);
      setComparisonFetchComplete(false);

      if (type === 'analytics') {
        if (panelChangesAbortRef.current) {
          panelChangesAbortRef.current.abort();
          panelChangesAbortRef.current = null;
        }
        setPanelChanges(null);
        setPanelChangesLoading(false);
      }

      try {
        if (isAutoRefresh) {
          setIsAutoRefreshing(true);
        } else {
          setIsLoading(true);
        }

        setCustomLoadingStatus(null);

        // Build request body - add reportingToken only for demand type
        const requestBody = {
          selection: selection,
          period: toAnalyticsPeriod(period, realtimeMinutes),
          ...(type === 'demand' && {reportingToken: auth.reportingToken, dataType: dataType}),
        };

        // Add analytics-specific filters
        if (type === 'analytics') {
          requestBody.report = 'getAnalytics';
          requestBody.filters = {
            country: analyticsCountry || null,
            device: analyticsDevice || null,
            bidder: analyticsBidder || null,
          };
          requestBody.groupByColumn = analyticsGrouping;
        }

        // getPrebidReport endpoint accepts type param as its used for prices reporting too
        if (type === 'prebid') {
          requestBody.type = 'stacked';
        }

        // Get endpoint based on stacked report type
        let endpoint;
        switch (type) {
          case 'analytics':
            endpoint = 'getAnalyticsData';
            break;
          case 'direct':
            endpoint = 'getDirectStackData';
            break;
          case 'prebid':
            endpoint = 'getPrebidReport';
            break;
          case 'demand':
            endpoint = 'getDemandStackData';
            break;
          default:
            throw new Error(`Unknown type: ${type}`);
        }

        const apiCall = {
          endpoint: endpoint,
          retryOnTimeout: true,
          onRetry: () => {
            setCustomLoadingStatus(
              `Please wait for a while longer, we are processing a lot of data...`
            );
          },
          options: {
            signal: abortController.signal,
            method: 'POST',
            body: JSON.stringify(requestBody),
            headers: {
              Authorization: `Bearer ${auth.token}`,
              'Cache-Control': 'max-age=0, no-cache',
              'Content-Type': 'application/json',
            },
          },
        };

        let response = await APICall(apiCall);

        // Check if request was aborted or if a new request started (controller changed)
        if (abortController.signal.aborted || abortControllerRef.current !== abortController) {
          return;
        }

        if (response.error) {
          throw new Error(response.error);
        }

        // Convert UTC date strings to local timezone show local hours
        const normalizedData =
          type === 'analytics' && Array.isArray(response.data)
            ? convertAnalyticsDatesToLocalTimezone(response.data, period)
            : response.data;

        setData(normalizedData);

        setInsights(response.insights);

        // Used in prebid and analytics types
        // For analytics, attributeGroups has nested structure: { success: true, data: { ... } }
        // Extract the actual attributeGroups from response.attributeGroups.data if it exists
        if (response.attributeGroups) {
          const attributeGroupsData =
            response.attributeGroups.data && typeof response.attributeGroups.data === 'object'
              ? response.attributeGroups.data
              : response.attributeGroups;
          setAttributeGroups(attributeGroupsData);
          setAnalyticsAttributeGroups('analyticsAttributeGroups', attributeGroupsData);
        }

        // Used in reseller statuses
        if (response.directSSPCodeAndAlias) {
          setResellerValues(response.directSSPCodeAndAlias);
        }

        if (type === 'prebid') {
          setSspTypeChanges(
            response.sspTypeChanges?.countsByDate ? response.sspTypeChanges : null
          );
        } else {
          setSspTypeChanges(null);
        }

        if (type === 'prebid' || type === 'demand') {
          setResellerCommission(response.reseller_commission ?? null);
        }

        // Main data is ready – unblock UI immediately
        if (isAutoRefresh) {
          setIsAutoRefreshing(false);
        } else {
          setIsLoading(false);
        }

        // Optionally fetch comparison period data for trends when comparison is enabled.
        // This runs in the background and does NOT block the main UI.
        const shouldFetchComparison =
          comparisonEnabled &&
          comparisonPeriod &&
          period?.label !== 'realtime' &&
          period?.label !== 'All history' &&
          !(type === 'analytics' && (period?.label === 'Last 24 hours' || period?.label === 'Today'));

        if (shouldFetchComparison) {
          (async () => {
            try {
              const comparisonRequestBody = {
                ...requestBody,
                period: comparisonPeriod,
              };

              const comparisonApiCall = {
                ...apiCall,
                options: {
                  ...apiCall.options,
                  body: JSON.stringify(comparisonRequestBody),
                },
              };

              const comparisonResponse = await APICall(comparisonApiCall);

              // Check if request was aborted or if a new request started
              if (
                abortController.signal.aborted ||
                abortControllerRef.current !== abortController
              ) {
                return;
              }

              if (!comparisonResponse.error && Array.isArray(comparisonResponse.data)) {
                const normalizedComparisonData =
                  type === 'analytics'
                    ? convertAnalyticsDatesToLocalTimezone(
                        comparisonResponse.data,
                        comparisonPeriod
                      )
                    : comparisonResponse.data;
                setComparisonData(normalizedComparisonData);
              }
              setComparisonFetchComplete(true);
            } catch (e) {
              if (e.name === 'AbortError') return;
              setComparisonFetchComplete(true);
            }
          })();
        } else {
          setComparisonFetchComplete(true);
        }
      } catch (error) {
        // Don't handle errors if the request was aborted
        if (error.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
          return;
        }
        const message = error.message || 'An error occurred';
        if (!isAutoRefresh) {
          callSnackbarSuccessError(false, '', message);
          setData({
            success: false,
            error: message,
          });
        }
        if (isAutoRefresh) {
          setIsAutoRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [
      setData,
      selection,
      period,
      comparisonPeriod,
      dataType,
      type,
      analyticsCountry,
      analyticsDevice,
      analyticsBidder,
      analyticsGrouping,
      realtimeMinutes,
      comparisonEnabled,
      setAnalyticsAttributeGroups,
      callSnackbarSuccessError,
    ]
  );

  useEffect(() => {
    fetchData();
    // Depend on the inputs that should refetch — not on `fetchData` itself.
    // `fetchData` is recreated every render because callSnackbarSuccessError is unstable;
    // listing [fetchData] caused an infinite request loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selection,
    period,
    comparisonPeriod,
    dataType,
    analyticsCountry,
    analyticsDevice,
    analyticsBidder,
    analyticsGrouping,
    realtimeMinutes,
    comparisonEnabled,
  ]);

  // Ad-setup change history is fetched after analytics data is ready so both heavy
  // queries don't hit the database at the same time. It does not depend on metric
  // filters (country/device/bidder).
  const fetchPanelChanges = useCallback(async () => {
    if (panelChangesAbortRef.current) {
      panelChangesAbortRef.current.abort();
      panelChangesAbortRef.current = null;
    }

    if (type !== 'analytics' || !analyticsChangeLogEnabled || !selection) {
      setPanelChanges(null);
      setPanelChangesLoading(false);
      return;
    }

    const token = usePersistentAuthStore.getState().auth?.token;
    if (!token) return;

    const abortController = new AbortController();
    panelChangesAbortRef.current = abortController;
    setPanelChangesLoading(true);

    try {
      const panelChangesResponse = await fetchPanelChangesBySelection({
        selection,
        period,
        realtimeMinutes,
        token,
        signal: abortController.signal,
      });

      if (abortController.signal.aborted || panelChangesAbortRef.current !== abortController) {
        return;
      }

      setPanelChanges(panelChangesResponse?.success ? panelChangesResponse : null);
    } catch (error) {
      if (error?.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }
      setPanelChanges(null);
    } finally {
      if (panelChangesAbortRef.current === abortController) {
        setPanelChangesLoading(false);
      }
    }
  }, [type, analyticsChangeLogEnabled, selection, period, realtimeMinutes]);

  useEffect(() => {
    if (type !== 'analytics' || !selection) {
      return undefined;
    }

    if (!analyticsChangeLogEnabled) {
      if (panelChangesAbortRef.current) {
        panelChangesAbortRef.current.abort();
        panelChangesAbortRef.current = null;
      }
      setPanelChanges(null);
      setPanelChangesLoading(false);
      return undefined;
    }

    if (isLoading || isAutoRefreshing || !Array.isArray(data)) {
      return undefined;
    }

    fetchPanelChanges();

    return () => {
      if (panelChangesAbortRef.current) {
        panelChangesAbortRef.current.abort();
        panelChangesAbortRef.current = null;
      }
    };
  }, [
    type,
    analyticsChangeLogEnabled,
    selection,
    period,
    realtimeMinutes,
    isLoading,
    isAutoRefreshing,
    data,
    fetchPanelChanges,
  ]);

  const handleAutoRefresh = useCallback(() => {
    fetchData({isAutoRefresh: true});
  }, [fetchData]);

  const handleExportDataReady = useCallback(payload => {
    setExportPayload(payload);
  }, []);

  useEffect(() => {
    if (!data || !Array.isArray(data) || data.length === 0 || data?.error) {
      setExportPayload(null);
    }
  }, [data]);

  const {progress: refreshProgress} = useAutoRefresh({
    enabled: isRealtime && autoRefreshEnabled,
    isLoading: isLoading || isAutoRefreshing,
    onRefresh: handleAutoRefresh,
    intervalMs: realtimeRefreshIntervalSeconds * 1000,
  });

  // Cleanup: abort any pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <Container maxWidth="xl">
      {isRealtime && autoRefreshEnabled && data && !isLoading && !isAutoRefreshing && (
        <LinearProgress
          variant="determinate"
          value={refreshProgress}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999999,
          }}
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" flexWrap="wrap" alignItems="center" width="100%" style={{gap: 0}}>
            <Box
              display="flex"
              flex="1 1 auto"
              minWidth={0}
              alignItems="center"
              flexWrap="wrap"
              style={{gap: 0}}
            >
              {type === 'demand' && <DataTypeSelector inDropdown={false} />}
              <PeriodPicker
                isMobile={isMobile}
                stackedReportType={type}
                autoRefreshLoading={isRealtime && (isLoading || isAutoRefreshing)}
              />
              {showServerClientToggle && (
                <Tooltip title="Group demand by the Prebid server and Prebid client side.">
                  <FormControlLabel
                    style={{margin: 0, paddingLeft: 8}}
                    control={
                      <Checkbox
                        checked={!groupingEnabled && prebidSplitMode === 'serverClient'}
                        color="primary"
                        onChange={() => {
                          const isChecked = !groupingEnabled && prebidSplitMode === 'serverClient';
                          if (isChecked) {
                            setAnalyticsAttributeGroups('groupingEnabled', true);
                          } else {
                            setAnalyticsAttributeGroups('prebidSplitMode', 'serverClient');
                            setAnalyticsAttributeGroups('groupingEnabled', false);
                          }
                        }}
                      />
                    }
                    label={<Typography variant="body2">Server/Client split</Typography>}
                  />
                </Tooltip>
              )}
              {type === 'prebid' && (
                <Tooltip title="Group demand by direct SSPs vs reseller SSPs.">
                  <FormControlLabel
                    style={{margin: 0, paddingLeft: 8}}
                    control={
                      <Checkbox
                        checked={!groupingEnabled && prebidSplitMode === 'directReseller'}
                        color="primary"
                        onChange={() => {
                          const isChecked = !groupingEnabled && prebidSplitMode === 'directReseller';
                          if (isChecked) {
                            setAnalyticsAttributeGroups('groupingEnabled', true);
                          } else {
                            setAnalyticsAttributeGroups('prebidSplitMode', 'directReseller');
                            setAnalyticsAttributeGroups('groupingEnabled', false);
                          }
                        }}
                      />
                    }
                    label={<Typography variant="body2">Direct/Reseller split</Typography>}
                  />
                </Tooltip>
              )}
            </Box>
            {data && Array.isArray(data) && data.length > 0 && !data?.error && (
              <Box flex="0 0 auto" ml="auto">
                <GraphControlsMenu
                  type={type}
                  period={period}
                  resellerCommission={resellerCommission}
                  panelChangesLoading={panelChangesLoading}
                  exportEnabled={Boolean(exportPayload?.graphData?.length)}
                  exportPayload={{...exportPayload, disabledSources}}
                  onExportXls={options =>
                    exportStackedReportXls({
                      ...exportPayload,
                      disabledSources,
                      period,
                      type,
                      options,
                      context: {
                        type,
                        selection,
                        period,
                        comparisonPeriod,
                        analyticsGrouping,
                        analyticsCountry,
                        analyticsDevice,
                        analyticsBidder,
                        groupingEnabled,
                        prebidSplitMode,
                      },
                    })
                  }
                />
              </Box>
            )}
          </Box>
        </Grid>
        {!data && (
          <Grid item xs={12}>
            <div style={{textAlign: 'center'}}>
              <FadeIn>
                <Typography variant={'caption'} display="block" gutterBottom>
                  {customLoadingStatus || 'Loading data'}
                </Typography>
                <CircularProgress />
              </FadeIn>
            </div>
          </Grid>
        )}
        {data?.length === 0 && !data?.error && (
          <Grid item xs={12}>
            <Container
              maxWidth="xl"
              style={{
                padding: 65,
                textAlign: 'center',
              }}
            >
              <Placeholder />
              <Typography align={'center'}>No data</Typography>
            </Container>
          </Grid>
        )}

        {data?.error && (
          <Grid item xs={12} style={{position: 'relative'}}>
            <FadeIn>
              <Typography variant={'caption'} display={'block'} align={'center'}>
                {data.error}
              </Typography>
            </FadeIn>
            {isLoading && !isAutoRefreshing && (
              <Backdrop
                open={true}
                style={{
                  position: 'absolute',
                  zIndex: 9999,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <CircularProgress />
              </Backdrop>
            )}
          </Grid>
        )}

        {data && Array.isArray(data) && data.length > 0 && !data?.error && (
          <Grid item xs={12} style={{position: 'relative'}}>
            <FadeIn>
              <StackedReportGraph
                type={type}
                data={data}
                comparisonData={comparisonData}
                comparisonFetchComplete={comparisonFetchComplete}
                period={period}
                comparisonPeriod={comparisonPeriod}
                insights={insights}
                attributeGroups={attributeGroups}
                resellerValues={resellerValues}
                resellerCommission={resellerCommission}
                panelChanges={panelChanges}
                sspTypeChanges={sspTypeChanges}
                prebidSplitMode={prebidSplitMode}
                login={auth}
                onExportDataReady={handleExportDataReady}
              />
            </FadeIn>
            {isLoading && !isAutoRefreshing && data && (
              <Backdrop
                open={true}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1000, // Below app bar (1100), sidebar (1200), and tabs (typically in AppBar context)
                  backgroundColor: theme.palette.background.default,
                  opacity: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </Backdrop>
            )}
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default StackedReport;
